import { cn } from '@app/ui/_lib/utils';
import type { ReactNode } from 'react';
import type { SanityImage } from '../types';
import { isValidRichText } from '../_lib/isValidRichText';
import { Anchor, type LinkShape } from '../Anchor';
import { IconResolver, type IconType } from '../IconResolver';
import { Text } from '../Text';
import { ButtonCtaAction, ButtonCtaActionProps, ComponentButton, ComponentButtonProps } from '../Inputs/Button';
import { Avatar } from '../Avatar';

export type ContactHeaderProps = {
	className?: string;
	image?: SanityImage;
	label?: string;
	title?: ReactNode;
	copy?: ReactNode;
	links?: Array<(LinkShape | ButtonCtaActionProps) & { icon?: IconType }>;
	buttons?: ComponentButtonProps[];
};

export function ContactHeader(props: ContactHeaderProps) {
	return (
		<div className={cn('flex flex-col gap-spacing-md md:flex-row', props.className)} data-component='ContactHeader'>
			<Avatar className='shrink-0' image={props.image} size='2xl' />

			<div className='flex flex-col gap-spacing-md'>
				<div className='flex flex-col gap-spacing-2xs'>
					{props.label && (
						<Text className='text-text-brand' styleType='label-md'>
							{props.label}
						</Text>
					)}

					{props.title && (
						<Text as='h2' styleType='heading-sm'>
							{props.title}
						</Text>
					)}
				</div>

				{isValidRichText(props.copy) && (
					<Text className='text-text-secondary' styleType='body-xl'>
						{props.copy}
					</Text>
				)}

				{props.links && props.links.length > 0 && (
					<ul className='flex flex-col gap-spacing-xs [overflow-wrap:anywhere]'>
						{props.links.map((item, index) => (
							<li key={`${item.icon}-${index}-${item.label}-${item.href}`} className='flex gap-spacing-2xs'>
								{item.icon && <IconResolver className='text-text-brand shrink-0 w-5 h-5' icon={item.icon} />}

								<Text
									className='text-text-primary transition-colors duration-fast hover:text-text-primary-hover'
									styleType='body-xl-medium'
								>
									{'ctaAction' in item ? <ButtonCtaAction {...item} /> : <Anchor href={item.href}>{item.label}</Anchor>}
								</Text>
							</li>
						))}
					</ul>
				)}

				{props.buttons && props.buttons.length > 0 && (
					<div className={`flex flex-wrap gap-4`}>
						{props.buttons.map((item, index) => (
							<ComponentButton key={`${item.label}-${index}-ContactHeader-buttons`} {...item} />
						))}
					</div>
				)}
			</div>
		</div>
	);
}
